import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Row, Col, Card, Button, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import './product-transfers.scss';
import { withSwal } from 'react-sweetalert2';
// components

import {
    ProductTransfer,
    ProductTransferMissingDataTypes,
    ProductTransferRegretReasons,
    ProductTransferRejectionReasons,
    TransferStatuses,
} from '../../interfaces/ProductTransfer';
import { PensionProduct, PensionProductTypes } from '../../interfaces/PensionProduct';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../utils/api/ApiError';
import {
    getProductTransfers,
    updateProductTransfer,
    getProductTransferCompanies,
    exportProductTransfers,
    uploadProductTransfers,
    uploadFileToProductTransfer,
    resubmitB2,
    deleteProductTransfer,
    createOneTimeDeposit,
    updateOneTimeDeposit,
    deleteOneTimeDeposit,
} from '../../utils/api/productTransfers';
import PageTitle from '../../components/PageTitle';
import Table from '../../components/Table';
import Loader from '../../components/Loader';
import LaddaButton, { EXPAND_LEFT } from 'react-ladda-button';
import MoneyColumn from '../../components/MoneyColumn';
import { addCommas } from '../../utils/addCommas';
import { FormInput } from '../../components';
import { APICore } from '../../utils/api/apiCore';
import { camelCaseToText } from '../../utils/camelCaseToText';
import { SignedDocument } from '../../interfaces/SignedDocument';
import GenerateForm from '../../components/generateForm';
import ClientAutocomplete from '../../components/ClientAutocomplete';
import { ClientFile } from '../../interfaces/ClientFile';
import DropDownColumn from '../../components/dropDowncolumn';
import DateColumn from '../../components/DateColumn';
import DatePicker from 'react-datepicker';
import { Pension } from '../../interfaces/Pension';
import { User } from '../../interfaces/User';
import { Company, CompanyTypes } from '../../interfaces/Company';
import { DateRangePicker } from 'rsuite';
import { DateRange } from 'rsuite/esm/DateRangePicker/types';
import { Fund } from '../../interfaces/Fund';
import Dropzone from 'react-dropzone';
import { FileType } from '../../interfaces/FileType';
import TranslatedColumn from '../../components/TranslatedColumn';
import i18n from 'i18next';
import { getCompanyName } from '../../utils/getCompanyName';
import { formatDate } from '../../utils/formatDate';
import useQueryStringSync from '../../hooks/useQueryStringSync';
import MailingButtonsAndLogic from './MailingButtonsAndLogic';
import ProductTransferFileUpload from './ProductTransferFileUpload';
import { EmploymentStatuses } from '../../interfaces/Client';
import globalErrorHandler from '../../utils/globalErrorHandler';
import AutoSavingInput from '../../components/AutoSavingFormInput';
import ClientColumn from '../../components/ClientColumn';
import { updateProduct } from '../../utils/api/products';
import { StatusTracker } from '../../interfaces/StatusTracker';
import { getUsers } from '../../utils/api/users';
import userOptionsForSelect from '../../utils/userOptionsForSelect';
import Select from 'react-select';
import { Option } from '../../interfaces/Option';

export interface Item {
    product: PensionProduct;
    signedDocument: SignedDocument;
}

// main component
const ProductTransfers = withSwal((props: any) => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const [search, setSearch] = useState('');
    const [clientSearch, setClientSearch] = useState(queryParams.get('clientId') || '');
    const { t } = useTranslation('productTransfer');
    const [loading, setLoading] = useState<boolean>(false);
    const [list, setList] = useState<Item[]>([]);
    const [total, setTotal] = useState(0);

    const [totalAnnualDeposit, setTotalAnnualDeposit] = useState(0);
    const [totalSavings, setTotalSavings] = useState(0);

    const [totalAgentCommissionSavings, setTotalAgentCommissionSavings] = useState(0);
    const [totalAgentCommissionProductivity, setTotalAgentCommissionProductivity] = useState(0);
    const [totalAgentMonthlyCommission, setTotalAgentMonthlyCommission] = useState(0);
    const [totalOneTimeDeposits, setTotalOneTimeDeposits] = useState(0);

    const [potentialSavings, setPotentialSavings] = useState(0);
    const [totalClients, setTotalClients] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(0);
    const [page, setPage] = useState(0);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [isLoadingExport, setIsLoadingExport] = useState(false);
    const [isLoadingImport, setIsLoadingImport] = useState(false);

    const oneTimeCommissionOptions = [
        { label: t('All'), value: '' },
        { label: '0', value: '0' },
        { label: '0-1000', value: '0-1000' },
        { label: '1000-2000', value: '1000-2000' },
        { label: '2000-3000', value: '2000-3000' },
        { label: '3000-4000', value: '3000-4000' },
        { label: '4000-5000', value: '4000-5000' },
        { label: '5000+', value: '5000' },
    ];

    // filters and sorting

    const [toCompany, setToCompany] = useState<string[]>(() => {
        const searchParams = new URLSearchParams(location.search);
        const companyParam = searchParams.get('toCompany') || searchParams.get('companyId') || searchParams.get('company');
        if (companyParam) {
            return companyParam.split(',').filter(Boolean);
        }
        return [];
    });
    const [status, setStatus] = useState<string[]>(() => {
        const statusParam = queryParams.get('status');
        if (statusParam) {
            return statusParam.split(',').filter(Boolean);
        }
        return [];
    });
    const [toProduct, setToProduct] = useState<string[]>(() => {
        const productParam = queryParams.get('toProduct');
        if (productParam) {
            return productParam.split(',').filter(Boolean);
        }
        return [];
    });
    const [active, setActive] = useState<string>(queryParams.get('active') || '');
    const [action, setAction] = useState<string[]>(() => {
        const actionParam = queryParams.get('action');
        if (actionParam) {
            return actionParam.split(',').filter(Boolean);
        }
        return [];
    });
    const [lastChangeStartDate, setLastChangeStartDate] = useState<Date | undefined>(
        queryParams.get('lastChangeStartDate') ? new Date(queryParams.get('lastChangeStartDate') as string) : undefined,
    );
    const [lastChangeEndDate, setLastChangeEndDate] = useState<Date | undefined>(
        queryParams.get('lastChangeEndDate') ? new Date(queryParams.get('lastChangeEndDate') as string) : undefined,
    );
    const [submissionStartDate, setSubmissionStartDate] = useState<Date | undefined>(
        queryParams.get('submissionStartDate') ? new Date(queryParams.get('submissionStartDate') as string) : undefined,
    );
    const [agentSignedStartDate, setAgentSignedStartDate] = useState<Date | undefined>(
        queryParams.get('agentSignedStartDate') ? new Date(queryParams.get('agentSignedStartDate') as string) : undefined,
    );
    const [agentSignedEndDate, setAgentSignedEndDate] = useState<Date | undefined>(
        queryParams.get('agentSignedEndDate') ? new Date(queryParams.get('agentSignedEndDate') as string) : undefined,
    );
    const [submissionEndDate, setSubmissionEndDate] = useState<Date | undefined>(
        queryParams.get('submissionEndDate') ? new Date(queryParams.get('submissionEndDate') as string) : undefined,
    );
    const [closeStartDate, setCloseStartDate] = useState<Date | undefined>(
        queryParams.get('closeStartDate') ? new Date(queryParams.get('closeStartDate') as string) : undefined,
    );
    const [closeEndDate, setCloseEndDate] = useState<Date | undefined>(
        queryParams.get('closeEndDate') ? new Date(queryParams.get('closeEndDate') as string) : undefined,
    );
    const [employmentStatus, setEmploymentStatus] = useState<string>(queryParams.get('employmentStatus') || '');
    const [isInvestmentRound, setIsInvestmentRound] = useState<string>(queryParams.get('isInvestmentRound') || '');
    const [agentIds, setAgentIds] = useState<string[]>(queryParams.get('agentId') ? (queryParams.get('agentId') as string).split(',') : []);
    const [oneTimeCommission, setOneTimeCommission] = useState<string>(queryParams.get('oneTimeCommission') || '');
    const [sortBy, setSortBy] = useState<string>(queryParams.get('sortBy') || '');
    const [asc, setAsc] = useState<boolean>(queryParams.get('asc') === 'true' || false);
    const [agents, setAgents] = useState<User[]>([]);
    const hasInitiallyLoaded = useRef(false);

    useQueryStringSync(
        useMemo(() => {
            const filters: any = {};

            if (status.length > 0) filters.status = status.join(',');
            if (toCompany.length > 0) filters.toCompany = toCompany.join(',');
            if (toProduct.length > 0) filters.toProduct = toProduct.join(',');
            if (active) filters.active = active;
            if (action.length > 0) filters.action = action.join(',');
            if (lastChangeStartDate) filters.lastChangeStartDate = lastChangeStartDate;
            if (lastChangeEndDate) filters.lastChangeEndDate = lastChangeEndDate;
            if (submissionStartDate) filters.submissionStartDate = submissionStartDate;
            if (submissionEndDate) filters.submissionEndDate = submissionEndDate;
            if (agentSignedStartDate) filters.agentSignedStartDate = agentSignedStartDate;
            if (agentSignedEndDate) filters.agentSignedEndDate = agentSignedEndDate;
            if (closeStartDate) filters.closeStartDate = closeStartDate;
            if (closeEndDate) filters.closeEndDate = closeEndDate;
            if (employmentStatus) filters.employmentStatus = employmentStatus;
            if (isInvestmentRound) filters.isInvestmentRound = isInvestmentRound;
            if (agentIds.length > 0) filters.agentId = agentIds.join(',');
            if (oneTimeCommission) filters.oneTimeCommission = oneTimeCommission;
            if (sortBy) filters.sortBy = sortBy;
            if (asc === true) filters.asc = asc;

            return filters;
        }, [
            status,
            toCompany,
            toProduct,
            active,
            action,
            lastChangeStartDate,
            lastChangeEndDate,
            submissionStartDate,
            submissionEndDate,
            agentSignedStartDate,
            agentSignedEndDate,
            closeStartDate,
            closeEndDate,
            employmentStatus,
            isInvestmentRound,
            agentIds,
            oneTimeCommission,
            sortBy,
            asc,
        ]),
        false,
    );

    const { user } = new APICore().getLoggedInUser();

    const statusOptions = Object.entries(TransferStatuses)
        .slice(3)
        .map((item) => ({
            label: t(camelCaseToText(item[0])),
            value: item[1],
        }));

    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const result = await getUsers({ status: 'all', limit: 1000 });
                const pensionAgents = result.users.filter(
                    (u: User) =>
                        u.roles.includes('agent') &&
                        (u.agentModules?.includes('pension') || u.agentModules?.some((module: string) => ['pension', 'investment'].includes(module))),
                );
                pensionAgents.sort((a: User, b: User) => {
                    if (a.isActive && !b.isActive) return -1;
                    if (!a.isActive && b.isActive) return 1;
                    return a.firstName.localeCompare(b.firstName);
                });

                setAgents(pensionAgents);
            } catch (err) {
                globalErrorHandler(err);
            }
        };
        fetchAgents();
    }, []);

    useEffect(() => {
        document.title = 'Product Transfers';

        return () => {
            document.title = 'Better Admin';
        };
    }, []);

    const onSent = (file: ClientFile) => {
        const tmpList = [...list];
        tmpList.forEach((item: Item) => {
            if (item.signedDocument?.file?._id === file._id) {
                item.signedDocument.file.sentToClientDate = new Date().toDateString();
            }
        });
        setList(tmpList);
    };

    const InvestmentPolicyColumn = ({ row }: { row: any }) => {
        const { product } = row.original;
        const { productTransfer } = product;
        return (
            <>
                {productTransfer.investmentPolicies.map((policy: any, i: number) => (
                    <div key={policy._id || i}>
                        {t(camelCaseToText((policy.fund as Fund).investmentPolicy))}{' '}
                        {policy.percentage !== 1 && `(${(policy.percentage * 100).toFixed(0)}%)`}
                    </div>
                ))}
            </>
        );
    };

    const SigningLinkColumn = ({ row }: { row: any }) => {
        const item = row.original;
        const { productTransfer } = item.product;
        return (
            <div data-test-id="signingLinkColumn">
                <GenerateForm signedDocument={item.signedDocument} productTransfer={productTransfer} onSent={onSent} />
            </div>
        );
    };

    const CommissionsColumn = ({ row }: { row: any }) => {
        const { productTransfer } = row.original.product;
        return (
            <div data-test-id="depositsColumn">
                {productTransfer.commissions.savings} / {productTransfer.commissions.deposit}
            </div>
        );
    };

    const FromColumn = ({ row }: { row: any }) => {
        const { product } = row.original;
        return (
            <div data-test-id="fromColumn">
                <div>{t(camelCaseToText(product.productType))}</div>
                <div>{getCompanyName(product.company.name)}</div>
                <div>{product.policyNumber}</div>
            </div>
        );
    };

    const ToColumn = ({ row }: { row: any }) => {
        const { product } = row.original;
        const { productTransfer } = product;
        if (productTransfer.action === 'agentAssignment') {
            return <div data-test-id="toColumn">---</div>;
        }
        return (
            <div data-test-id="toColumn">
                <div>{t(camelCaseToText(productTransfer.productType))}</div>
                <div>{getCompanyName(productTransfer.company.name)}</div>
                <div>{productTransfer.newPolicyNumber}</div>
            </div>
        );
    };

    const saveProductTransfer = async (productTransfer: ProductTransfer, update: any) => {
        try {
            const result = await updateProductTransfer(productTransfer._id, update);
            const index = list.findIndex((item) => item.product.productTransfer._id === productTransfer._id);
            if (index >= 0) {
                list[index].product.productTransfer = fixDates(result.productTransfer);
                if (result.productTransfer.submissionDate) {
                    list[index].product.productTransfer.submissionDate = new Date(result.productTransfer.submissionDate);
                }

                if (result.productTransfer.closeDate) {
                    list[index].product.productTransfer.closeDate = new Date(result.productTransfer.closeDate);
                }
                setList([...list]);
            }
        } catch (err: any) {
            globalErrorHandler(err);
        }
    };

    enum YesNo {
        Yes = 'true',
        No = 'false',
    }

    // @ts-ignore
    const DateColumnWithUpdate = ({ productTransfer, list, setList }) => {
        const [isUpdating, setIsUpdating] = useState(false);
        const handleUpdateClick = async () => {
            try {
                setIsUpdating(true);
                await updateProductTransfer(productTransfer._id, {
                    lastChangeDate: true,
                });
                const index = list.findIndex(
                    (item: { product: { productTransfer: { _id: any } } }) => item.product.productTransfer._id === productTransfer._id,
                );
                if (index >= 0) {
                    const newList = [...list];
                    newList[index].product.productTransfer.lastChangeDate = new Date();
                    setList(newList);
                }
                setIsUpdating(false);
            } catch (error) {
                setIsUpdating(false);
                globalErrorHandler(error);
            }
        };

        return (
            <div>
                <div>{formatDate(productTransfer.lastChangeDate, true)}</div>
                <div>
                    <Button onClick={handleUpdateClick} size="sm">
                        {isUpdating ? 'Updating...' : 'Update'}
                    </Button>
                </div>
            </div>
        );
    };

    const ExpandedRow = (rowProps: { row: any }) => {
        const { row } = rowProps;
        const [product, setProduct] = useState<PensionProduct>(row.original.product);

        // Helper function to convert string dates to Date objects
        const normalizeDates = (pt: ProductTransfer): ProductTransfer => {
            const normalized = { ...pt };

            // Convert date string fields to Date objects for DatePicker
            if (normalized.estimatedTransferDate && typeof normalized.estimatedTransferDate === 'string') {
                normalized.estimatedTransferDate = new Date(normalized.estimatedTransferDate) as any;
            }
            if (normalized.closeDate && typeof normalized.closeDate === 'string') {
                normalized.closeDate = new Date(normalized.closeDate) as any;
            }
            if (normalized.agentSignatureDate && typeof normalized.agentSignatureDate === 'string') {
                normalized.agentSignatureDate = new Date(normalized.agentSignatureDate) as any;
            }
            if (normalized.expirationDate && typeof normalized.expirationDate === 'string') {
                normalized.expirationDate = new Date(normalized.expirationDate) as any;
            }

            // Normalize dates in oneTimeDeposits array
            if (normalized.oneTimeDeposits && Array.isArray(normalized.oneTimeDeposits)) {
                normalized.oneTimeDeposits = normalized.oneTimeDeposits.map((deposit) => ({
                    ...deposit,
                    date: deposit.date && typeof deposit.date === 'string' ? new Date(deposit.date) : deposit.date,
                }));
            }

            return normalized;
        };

        const [productTransfer, setProductTransfer] = useState<ProductTransfer>(normalizeDates(product.productTransfer));
        const [statusTrackers, setStatusTrackers] = useState<StatusTracker[]>(row.original.statusTrackers);
        const [pension, setPension] = useState<Pension | undefined>(product?.pension as Pension);
        const [agent, setAgent] = useState<User | undefined>(pension?.agent as User);
        const [timeout, setTimeout] = useState<number>();
        const [isLoadingSubmitB2, setIsLoadingSubmitB2] = useState(false);
        // const [productTransfer, setProductTransfer] = useState<ProductTransfer>(product.productTransfer);
        const monthlyDepositAmount =
            productTransfer.monthlyDepositAmount || product.newProductDepositData?.monthlyDepositAmount || product.deposits.averageMonthlyDeposit;

        const onDateChange = (date: Date, fieldName: string) => {
            saveProductTransfer({ [fieldName]: date });
        };

        useEffect(() => {
            list[row.index].product.productTransfer = productTransfer;
            setList(list);

            if (product.productTransfer.agent) {
                const agent = agents.find((a) => a._id === (product.productTransfer.agent as string));
                if (agent) {
                    setAgent(agent);
                }
            }
        }, [productTransfer]);

        const onResubmitB2 = async () => {
            setIsLoadingSubmitB2(true);
            try {
                const result = await resubmitB2(product._id);
            } catch (err: any) {
                globalErrorHandler(err);
            } finally {
                setIsLoadingSubmitB2(false);
            }
        };

        const onDeleteProductTransfer = async () => {
            const result = await props.swal.fire({
                title: t('Are you sure?'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('Yes, delete it!'),
                cancelButtonText: t('Cancel'),
                confirmButtonColor: '#dc3545',
            });

            if (result.isConfirmed) {
                try {
                    await deleteProductTransfer(productTransfer._id);
                    props.swal.fire({
                        title: t('The product transfer has been deleted.'),
                        icon: 'success',
                    });

                    // Remove the item from the list
                    const updatedList = list.filter((item) => item.product.productTransfer._id !== productTransfer._id);
                    setList(updatedList);
                    setTotal(total - 1);
                } catch (err: any) {
                    globalErrorHandler(err);
                }
            }
        };

        const saveProductTransfer = async (update: any) => {
            if (!productTransfer) return;

            try {
                setProductTransfer({ ...productTransfer, ...update });

                const result = await updateProductTransfer(productTransfer._id, update);

                const tmpList = [...list];
                const index = tmpList.findIndex((item) => item.product._id === product._id);
                tmpList[index].product.productTransfer.status = result.productTransfer.status;

                // setList(tmpList);

                // setProductTransfer(result.productTransfer);
            } catch (err: any) {
                setProductTransfer(productTransfer);
                globalErrorHandler(err);
            }
        };

        const saveProduct = async (update: any) => {
            if (!product) return;
            try {
                setProduct({ ...product, ...update });
                const result = await updateProduct(product._id, update);

                // const tmpList = [...list];
                // const index = tmpList.findIndex((item) => item.product._id === product._id);
                // tmpList[index].product[update] = result.productTransfer.status;
            } catch (err: any) {
                globalErrorHandler(err);
            }
        };
        const onTextFieldChanged = (value: string, key: string) => {
            setProductTransfer({ ...productTransfer, [key]: value });
            if (timeout) {
                window.clearTimeout(timeout);
            }
            const tmp = window.setTimeout(() => {
                saveProductTransfer({ [key]: value });
            }, 5000);
            setTimeout(tmp);
        };

        const ProductTransferDropDown = (params: { productTransfer: ProductTransfer; name: string; options: any; placeholder: string }) => {
            const [pt, setPT] = useState<any>(productTransfer);

            useEffect(() => {
                setPT(productTransfer);
            }, [productTransfer]);

            return (
                <Dropdown className="select-action" align={i18n.dir() === 'ltr' ? 'start' : 'end'}>
                    <Dropdown.Toggle variant="light">
                        {pt[params.name] === undefined
                            ? params.placeholder
                            : pt[params.name] === 'true' || pt[params.name] === true
                              ? t('Yes')
                              : pt[params.name] === 'false' || pt[params.name] === false
                                ? t('No')
                                : t(camelCaseToText(pt[params.name]))}
                        <i className="mdi mdi-chevron-down"></i>
                    </Dropdown.Toggle>
                    <Dropdown.Menu data-test-id="select">
                        {Object.entries(params.options).map(([key, value], i) => (
                            <Dropdown.Item
                                key={key}
                                onClick={() =>
                                    saveProductTransfer({
                                        [params.name]: value === 'true' ? true : value === 'false' ? false : value,
                                    })
                                }
                            >
                                {t(camelCaseToText(key))}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            );
        };

        const getAgentPayments = (): any[] => {
            const oneTime = productTransfer?.commissions?.agentCommissions?.oneTime?.received || [];
            const monthly = productTransfer?.commissions?.agentCommissions?.monthly?.received || [];
            const list = [
                ...oneTime.map((item: any) => ({
                    ...item,
                    type: 'oneTime',
                    date: new Date(item.date),
                })),
                ...monthly.map((item: any) => ({
                    ...item,
                    type: 'monthly',
                    date: new Date(item.date),
                })),
            ];
            list.sort((a, b) => a.date.getTime() - b.date.getTime());
            return list;
        };

        const onDepositChanged = async (index: number, value: string) => {
            if (isNaN(parseFloat(value))) {
                return;
            }

            const deposit = productTransfer.oneTimeDeposits[index];
            const depositId = deposit._id;

            try {
                let result;

                // If deposit doesn't have an ID, it's new - create it
                if (!depositId) {
                    result = await createOneTimeDeposit(productTransfer._id, {
                        amount: parseFloat(value),
                        date: deposit.date || new Date(),
                    });
                } else {
                    // If deposit has an ID, update it
                    result = await updateOneTimeDeposit(productTransfer._id, depositId, {
                        amount: parseFloat(value),
                    });
                }

                // Update local state with the response
                const updatedList = [...list];
                const itemIndex = updatedList.findIndex((item) => item.product._id === product._id);
                if (itemIndex >= 0) {
                    updatedList[itemIndex].product.productTransfer = result.productTransfer;
                    setList(updatedList);
                    setProductTransfer(normalizeDates(result.productTransfer));
                }
            } catch (err: any) {
                globalErrorHandler(err);
            }
        };

        const onAddDeposit = (e: any) => {
            // Check if there's already a deposit with amount 0
            if (productTransfer.oneTimeDeposits?.length > 0) {
                const lastDeposit = productTransfer.oneTimeDeposits[productTransfer.oneTimeDeposits.length - 1];
                if (lastDeposit?.amount === 0) {
                    return;
                }
            }

            // Add deposit to local state only - don't call API yet
            const tmp = { ...productTransfer };
            if (!tmp.oneTimeDeposits) {
                tmp.oneTimeDeposits = [];
            }

            tmp.oneTimeDeposits.push({
                amount: 0,
                date: new Date(),
                // Note: no _id yet - will be created when user enters amount
            });

            setProductTransfer(tmp);
        };

        const onDeleteDeposit = async (index: number) => {
            const deposit = productTransfer.oneTimeDeposits[index];
            const depositId = deposit._id;

            // If deposit doesn't have an ID, it only exists locally - just remove it
            if (!depositId) {
                const tmp = { ...productTransfer };
                tmp.oneTimeDeposits = tmp.oneTimeDeposits.filter((_, i) => i !== index);
                setProductTransfer(tmp);
                return;
            }

            const result = await props.swal.fire({
                title: t('Are you sure?'),
                text: t('This will delete the deposit and its associated product transfer'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('Yes, delete it!'),
                cancelButtonText: t('Cancel'),
                confirmButtonColor: '#dc3545',
            });

            if (result.isConfirmed) {
                try {
                    const deleteResult = await deleteOneTimeDeposit(productTransfer._id, depositId);

                    // Update local state with the response
                    const updatedList = [...list];
                    const itemIndex = updatedList.findIndex((item) => item.product._id === product._id);
                    if (itemIndex >= 0) {
                        updatedList[itemIndex].product.productTransfer = deleteResult.productTransfer;
                        setList(updatedList);
                        setProductTransfer(normalizeDates(deleteResult.productTransfer));
                    }

                    props.swal.fire({
                        title: t('Deleted!'),
                        text: t('The deposit has been deleted.'),
                        icon: 'success',
                    });
                } catch (err: any) {
                    globalErrorHandler(err);
                }
            }
        };

        return (
            <>
                <div className="product-transfer-expanded">
                    <div className="data-div">
                        <Row>
                            <Col xl={2} xs={2}>
                                <span className="category-name">{t('Monthly deposit')}</span>
                                <div className="category-value">₪{addCommas(monthlyDepositAmount || 0)}</div>
                            </Col>
                            <Col xl={2} xs={2}>
                                <span className="category-name">{t('Annual deposit')}</span>
                                <div className="category-value">₪{addCommas((monthlyDepositAmount || 0) * 12)}</div>
                            </Col>
                            {(productTransfer.action === 'newProduct' || productTransfer.employmentStatus === 'selfEmployed') && (
                                <Col xl={2} xs={2} className="deposits">
                                    <table>
                                        <tbody>
                                            <tr>
                                                <td>{t('One time deposits')}</td>
                                                <td>
                                                    {productTransfer.action !== 'newDeposit' && (
                                                        <span onClick={onAddDeposit} className="add-deposit">
                                                            {t('add deposit')}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                            {productTransfer.oneTimeDeposits.map((deposit, index) => (
                                                <tr key={deposit._id || index}>
                                                    <td>
                                                        <AutoSavingInput
                                                            type="text"
                                                            containerClass="one-time-deposit"
                                                            value={deposit.amount}
                                                            name="deposit.amount"
                                                            className={'btn btn-light policy-input'}
                                                            onSave={(value) => onDepositChanged(index, value)}
                                                            currency={true}
                                                        />
                                                    </td>
                                                    <td> {formatDate(deposit.date, true)} </td>
                                                    <td>
                                                        <i
                                                            className="uil uil-trash-alt font-20 cursor-pointer text-danger"
                                                            onClick={() => onDeleteDeposit(index)}
                                                            title={t('Delete deposit')}
                                                        ></i>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Col>
                            )}
                            <Col xl={2} xs={2}>
                                <span className="category-name">{t('Agent')}</span>
                                <div className="category-value">{agent?.fullName}</div>
                            </Col>
                            <Col xl={2} xs={2} data-test-id="active-col">
                                <span className="category-name">{t('Active')}</span>
                                <div className="category-value">
                                    {t(product.isDeclaredActive ? 'Yes' : 'No')},{' '}
                                    {t(productTransfer.isActive ? 'Pending first deposit' : 'No deposits expected')}
                                </div>
                            </Col>
                            <Col xl={2} xs={2}>
                                <span className="category-name">{t('Signed on')}</span>
                                <div className="category-value">
                                    {productTransfer.agentSignatureDate ? formatDate(productTransfer.agentSignatureDate) : '-'}
                                </div>
                            </Col>
                            <Col xl={2} xs={2}>
                                <span className="category-name">{t('Employment status')}</span>
                                <div className="category-value">{t(camelCaseToText(productTransfer.employmentStatus))}</div>
                            </Col>
                            {!!product.client.employer?.name && (
                                <Col xl={2} xs={2}>
                                    <span className="category-name">{t('Employer')}</span>
                                    <div className="category-value">{product.client.employer.name}</div>
                                </Col>
                            )}
                            <Col xl={2} xs={2}>
                                <span className="category-name">{t('Expiration date')}</span>
                                <div className="category-value">
                                    {productTransfer.expirationDate ? formatDate(productTransfer.expirationDate) : '-'}
                                </div>
                            </Col>
                            {[PensionProductTypes.comprehensivePensionFund, PensionProductTypes.complementaryPensionFund].includes(
                                productTransfer.productType,
                            ) && (
                                <>
                                    <Col xl={2} xs={2}>
                                        <span className="category-name">{t('Risk insurance')}</span>
                                        <div className="category-value">{productTransfer.insurance.riskPercentage}%</div>
                                    </Col>

                                    <Col xl={2} xs={2}>
                                        <span className="category-name">{t('Give up risk insurance')}</span>
                                        <div className="category-value">{t(product.flags.includes('giveUpSpouseInsurance') ? 'Yes' : 'No')}</div>
                                    </Col>

                                    <Col xl={2} xs={2}>
                                        <span className="category-name">{t('Disability insurance')}</span>
                                        <div className="category-value">{productTransfer.insurance.disabilityPercentage}%</div>
                                    </Col>
                                </>
                            )}
                            <Col xl={2} xs={2}>
                                <span className="category-name">{t('New Policy Number')}</span>
                                <div>
                                    <AutoSavingInput
                                        type="text"
                                        containerClass=""
                                        value={productTransfer.newPolicyNumber}
                                        name="productTransfer.newPolicyNumber"
                                        className={'btn btn-light policy-input'}
                                        onSave={(value) => saveProductTransfer({ newPolicyNumber: value })}
                                    />
                                </div>
                            </Col>
                            <Col xl={2} xs={2}>
                                <span className="category-name">{t('Estimated transfer date')}</span>
                                <DatePicker
                                    name="dueDate"
                                    selected={productTransfer.estimatedTransferDate}
                                    onChange={(date: Date) => onDateChange(date, 'estimatedTransferDate')}
                                    dateFormat={'dd/MM/yyyy'}
                                    placeholderText={t('select date')}
                                    customInput={<input className="btn btn-light date-input" data-test-id="estimatedTransferDateInput" />}
                                />
                                {/*// <span*/}
                                {/*//     className="category-value">*/}
                                {/*//*/}
                                {/*//     {formatDate(productTransfer.estimatedTransferDate)}</span> : null}*/}
                            </Col>
                            <Col xl={2} xs={2}>
                                <span className="category-name">{t('Close date')}</span>
                                <DatePicker
                                    name="dueDate"
                                    className="date"
                                    selected={productTransfer.closeDate}
                                    onChange={(date: Date) => onDateChange(date, 'closeDate')}
                                    dateFormat={'dd/MM/yyyy'}
                                    placeholderText={t('select date')}
                                    customInput={<input className="btn btn-light date-input" />}
                                />
                            </Col>
                            <Col xl={2} xs={2}>
                                <span data-test-id="isRejectedDropDown" className="category-name">
                                    {t('Rejected')}{' '}
                                </span>
                                <ProductTransferDropDown productTransfer={productTransfer} name="isRejected" options={YesNo} placeholder="" />
                            </Col>
                            {productTransfer.isRejected && (
                                <Col xl={2} xs={2}>
                                    <span data-test-id="rejectionReason" className="category-name">
                                        {t('Rejection reason')}
                                    </span>
                                    {/*<div><textarea value={productTransfer.rejectionReason} /></div>*/}
                                    <ProductTransferDropDown
                                        productTransfer={productTransfer}
                                        name="rejectionReason"
                                        options={ProductTransferRejectionReasons}
                                        placeholder={t('Select reason')}
                                    />
                                </Col>
                            )}
                            <Col xl={2} xs={2}>
                                <span data-test-id="missingData" className="category-name">
                                    {t('Missing Data')}
                                </span>
                                <ProductTransferDropDown
                                    productTransfer={productTransfer}
                                    name="missingDataType"
                                    options={ProductTransferMissingDataTypes}
                                    placeholder={t('Select missing data')}
                                />
                            </Col>
                            <Col xl={2} xs={2}>
                                <span data-test-id="missingData" className="category-name">
                                    {t('Missing Data Details')}
                                </span>
                                <div className="missing-data-details">{productTransfer.missingDataDetails}</div>
                            </Col>
                            <Col xl={2} xs={2}>
                                <span data-test-id="regret" className="category-name">
                                    {t('Regret')}
                                </span>
                                <ProductTransferDropDown productTransfer={productTransfer} name="isRegret" options={YesNo} placeholder={t('No')} />
                            </Col>
                            {productTransfer.isRegret && (
                                <Col xl={2} xs={2}>
                                    <span data-test-id="regretReason" className="category-name">
                                        {t('Regret reason')}
                                    </span>
                                    <ProductTransferDropDown
                                        productTransfer={productTransfer}
                                        name="regretReason"
                                        options={ProductTransferRegretReasons}
                                        placeholder={t('Select reason')}
                                    />
                                </Col>
                            )}
                            <Col xl={2} xs={2}>
                                <span data-test-id="reminder" className="category-name">
                                    {t('Send employer reminder')}
                                    {!!productTransfer.depositReminderDates.length && (
                                        <span>
                                            {' '}
                                            ({t('Sent on')}{' '}
                                            {formatDate(productTransfer.depositReminderDates[productTransfer.depositReminderDates.length - 1], true)})
                                        </span>
                                    )}
                                </span>
                                {productTransfer.isActive &&
                                productTransfer.employmentStatus === 'employee' &&
                                productTransfer.action !== 'agentAssignment' ? (
                                    <ProductTransferDropDown
                                        productTransfer={productTransfer}
                                        name="sendDepositReminderToEmployer"
                                        options={YesNo}
                                        placeholder={t('Send employer reminder')}
                                    />
                                ) : (
                                    <div>
                                        <b>{t('Not relevant')}</b>
                                    </div>
                                )}
                            </Col>

                            <Col xl={2} xs={2}>
                                <span className="category-name">{t('Comments')}</span>
                                <div>
                                    <textarea
                                        className="btn btn-light policy-input"
                                        placeholder={t('enter comments')}
                                        value={productTransfer.comments}
                                        onChange={(e: any) => onTextFieldChanged(e.target.value, 'comments')}
                                    />
                                </div>
                            </Col>
                            <Col xl={2} xs={2}>
                                <span className="category-name">{t('Validated fields')}</span>
                                <div>
                                    {!productTransfer.fieldValidation.length && (
                                        <div>
                                            <b>{t('No fields validated')}</b>
                                        </div>
                                    )}
                                    {productTransfer.fieldValidation.map((field: any, i: number) => (
                                        <div>
                                            {t(field.fieldName)}:{' '}
                                            <span className={field.isValid ? 'valid-field' : 'invalid-field'}>
                                                <b>{t(field.isValid ? 'Valid' : 'Invalid')}</b>
                                                {!field.isValid && (
                                                    <>{` (${t('expected')}: ${typeof field.expectedValue === 'number' ? addCommas(field.expectedValue) : field.expectedValue}, ${t('actual')}: ${typeof field.actualValue === 'number' ? addCommas(field.actualValue) : field.actualValue})`}</>
                                                )}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                            {[PensionProductTypes.comprehensivePensionFund, PensionProductTypes.complementaryPensionFund].includes(
                                productTransfer.productType,
                            ) && (
                                <Col xl={2} xs={2}>
                                    <span className="category-name">{t('Employer approval')}</span>
                                    <div className="category-value">{t(camelCaseToText(productTransfer.employerApprovalStatus))}</div>
                                </Col>
                            )}
                            <Col xl={2} xs={2}>
                                <span className="category-name">{t('Pension round')}</span>
                                <div className="category-value">{pension?.round}</div>
                            </Col>
                            {productTransfer.action === 'agentAssignment' && (
                                <Col xl={2} xs={2}>
                                    <span className="category-name">{t('Resend to Mislaka')}</span>
                                    <br />
                                    <LaddaButton
                                        data-test-id="resubmit-b2"
                                        loading={isLoadingSubmitB2}
                                        data-style={EXPAND_LEFT}
                                        className={'ladda-button btn btn-primary grouped-btn negative right '}
                                        dir="ltr"
                                        onClick={() => onResubmitB2()}
                                    >
                                        {!isLoadingSubmitB2 ? <i className="mdi mdi-upload me-1" /> : null}
                                        {t('Send')}
                                    </LaddaButton>
                                </Col>
                            )}
                            {productTransfer.status === TransferStatuses.SignedByAgent && (
                                <Col xl={2} xs={2}>
                                    <ProductTransferFileUpload productTransfer={productTransfer} setList={setList} list={list} />
                                </Col>
                            )}
                            {productTransfer.action === 'newDeposit' && (
                                <Col xl={2} xs={2}>
                                    <span className="category-name">{t('Delete Product Transfer')}</span>
                                    <br />
                                    <Button variant="danger" size="sm" onClick={onDeleteProductTransfer} data-test-id="delete-product-transfer">
                                        <i className="mdi mdi-delete me-1" />
                                        {t('Delete')}
                                    </Button>
                                </Col>
                            )}
                            <Col xl={2} xs={2}>
                                <div className="table-wrapper">
                                    <table className="commissions">
                                        <thead>
                                            <tr>
                                                <th>{t('Date')}</th>
                                                <th>{t('Status')}</th>
                                                <th>{t('By')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {statusTrackers.map((statusTracker: StatusTracker, index: number) => (
                                                <tr key={`${statusTracker.newStatus}-${statusTracker.createdAt}-${index}`}>
                                                    <td>{formatDate(statusTracker.createdAt)}</td>
                                                    <td>{t(camelCaseToText(statusTracker.newStatus))}</td>
                                                    <td>{statusTracker.byReport ? t('Report') : statusTracker.changer?.fullName}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Col>
                            {api.getLoggedInUser().user.isAdmin && (
                                <>
                                    <Col xl={2} xs={2}></Col>
                                    <Col xl={2} xs={2}>
                                        <div className="table-wrapper">
                                            <table className="commissions">
                                                <thead>
                                                    <tr>
                                                        <th>{t('One Time Commission Type')}</th>
                                                        <th>{t('Parameter')}</th>
                                                        <th>{t('Amount')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {productTransfer.commissions.agentCommissions.oneTime.productivity ? (
                                                        <tr>
                                                            <td>{t('Deposit One Time')}</td>
                                                            <td>
                                                                {Math.round(
                                                                    productTransfer.commissions.agentCommissions.oneTime.productivityFactor * 10000,
                                                                ) / 10000}
                                                            </td>
                                                            <td>₪{addCommas(productTransfer.commissions.agentCommissions.oneTime.productivity)}</td>
                                                        </tr>
                                                    ) : null}
                                                    <tr>
                                                        <td>{t('Savings One Time')}</td>
                                                        <td>
                                                            {Math.round(productTransfer.commissions.agentCommissions.oneTime.savingsFactor * 10000) /
                                                                10000}
                                                        </td>
                                                        <td>₪{addCommas(productTransfer.commissions.agentCommissions.oneTime.savings)}</td>
                                                    </tr>
                                                    {productTransfer.commissions.agentCommissions.oneTime.productivity ? (
                                                        <tr className="total-onetime">
                                                            <td>{t('Total One Time')}</td>
                                                            <td></td>
                                                            <td>₪{addCommas(productTransfer.commissions.agentCommissions.oneTime.estimatedTotal)}</td>
                                                        </tr>
                                                    ) : null}
                                                    <tr>
                                                        <td>{t('Monthly')}</td>
                                                        <td>
                                                            {Math.round(productTransfer.commissions.agentCommissions.monthly.monthlyFactor * 10000) /
                                                                10000}
                                                        </td>
                                                        <td>₪{addCommas(productTransfer.commissions.agentCommissions.monthly.estimatedTotal)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>{t('Expected payment date')}</td>
                                                        <td></td>
                                                        <td>{formatDate(new Date())}</td>
                                                    </tr>
                                                    {getAgentPayments().map((payment: any, i: number) => (
                                                        <tr>
                                                            <td>{formatDate(payment.date)}</td>
                                                            {payment.type === 'oneTime' ? <td>₪{addCommas(payment.amount)}</td> : <td></td>}
                                                            {payment.type === 'monthly' ? <td>₪{addCommas(payment.amount)}</td> : <td></td>}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </Col>
                                </>
                            )}
                        </Row>
                    </div>
                </div>
            </>
        );
    };

    const TotalSavingsColumn = (params: any) => {
        const { product } = params.row.original;
        let totalSavings = product.totalSavings || 0;

        return (
            <div className="d-flex justify-content-center">
                {!totalSavings ? '-' : ''}
                {totalSavings && totalSavings >= 0 ? `₪${addCommas(totalSavings)}` : ''}
                {totalSavings && totalSavings < 0 ? `-₪${addCommas(-totalSavings)}` : ''}
            </div>
        );
    };

    const columns = [
        {
            Header: t('Client'),
            accessor: 'product.client.fullName',
            sort: true,
            classes: 'table-productTransfer',
            Cell: ClientColumn,
        },
        {
            Header: t('Action'),
            accessor: 'product.productTransfer.action',
            sort: true,
            classes: 'table-productTransfer',
            Cell: TranslatedColumn(t),
        },
        {
            Header: t('Status'),
            accessor: 'product.productTransfer.status',
            sort: true,
            classes: 'table-client',
            Cell: DropDownColumn(statusOptions, (status, row) => saveProductTransfer(row.product.productTransfer, { status })),
        },
        {
            Header: t('Last change'),
            accessor: 'product.productTransfer.lastChangeDate',
            sort: true,
            classes: 'table-client',
            // @ts-ignore
            Cell: ({ row }) => <DateColumnWithUpdate productTransfer={row.original.product.productTransfer} list={list} setList={setList} />,
        },
        {
            Header: t('Agent signed date'),
            accessor: 'product.productTransfer.agentSignatureDate',
            sort: true,
            classes: 'table-client',
            Cell: DateColumn,
        },
        {
            Header: t('Submission Date'),
            accessor: 'product.productTransfer.submissionDate',
            sort: true,
            classes: 'table-client',
            Cell: DateColumn,
        },
        {
            Header: t('From'),
            accessor: 'product.productType',
            sort: false,
            classes: 'table-productTransfer',
            Cell: FromColumn,
        },
        {
            Header: t('To'),
            accessor: 'product.productTransfer.productType',
            sort: false,
            classes: 'table-productTransfer',
            Cell: ToColumn,
        },
        {
            Header: t('Com.'),
            accessor: 'product.productTransfer.commissions.deposit',
            classes: 'table-productTransfer',
            Cell: CommissionsColumn,
        },
        {
            Header: t('Total savings'),
            accessor: 'product.totalSavings',
            sort: false,
            classes: 'table-productTransfer',
            Cell: TotalSavingsColumn,
        },
        {
            Header: t('Investment policy'),
            accessor: 'product.productTransfer.investmentPolicies',
            sort: true,
            classes: 'table-productTransfer',
            Cell: InvestmentPolicyColumn,
        },
        {
            Header: t('Estimated transfer date'),
            accessor: 'product.productTransfer.estimatedTransferDate',
            sort: true,
            classes: 'table-client',
            Cell: DateColumn,
        },
        {
            Header: t('Links'),
            accessor: 'product.productTransfer.signEasy',
            sort: false,
            classes: 'table-productTransfer',
            Cell: SigningLinkColumn,
        },
    ];

    const api = new APICore();
    if (!api.canHandleSensitiveData()) {
        columns.splice(columns.length - 1, 1);
    }

    const fixDates = (productTransfer: ProductTransfer) => {
        if (productTransfer.closeDate) {
            productTransfer.closeDate = new Date(productTransfer.closeDate);
        }
        if (productTransfer.estimatedTransferDate) {
            productTransfer.estimatedTransferDate = new Date(productTransfer.estimatedTransferDate);
        }

        if (productTransfer.submissionDate) {
            console.log('productTransfer.submissionDate', productTransfer.submissionDate);
            productTransfer.submissionDate = new Date(productTransfer.submissionDate);
        }

        if (productTransfer.agentSignatureDate) {
            productTransfer.agentSignatureDate = new Date(productTransfer.agentSignatureDate);
        }
        return productTransfer;
    };

    const handleProductTransfers = async (page: number) => {
        setPage(page);

        const params = {
            page,
            search,
            ...(toCompany.length > 0 && { companyId: toCompany.join(',') }),
            productType: toProduct.join(','),
            isActive: active,
            action: action.join(','),
            status: status.join(','),
            clientId: clientSearch,
            fromSubmissionDate: submissionStartDate?.toISOString(),
            toSubmissionDate: submissionEndDate?.toISOString(),
            fromLastChangeDate: lastChangeStartDate?.toISOString(),
            toLastChangeDate: lastChangeEndDate?.toISOString(),
            fromAgentSignatureDate: agentSignedStartDate?.toISOString(),
            toAgentSignatureDate: agentSignedEndDate?.toISOString(),
            fromCloseDate: closeStartDate?.toISOString(),
            toCloseDate: closeEndDate?.toISOString(),
            employmentStatus,
            isInvestmentRound,
            agentId: agentIds.join(','),
            oneTimeCommission,
            sortBy,
            asc,
        };
        if (params.sortBy === 'fullName') {
            params.sortBy = 'client';
        }

        try {
            setLoading(true);
            const {
                list,
                total,
                itemsPerPage,
                totalAgentCommissionSavings,
                totalAgentCommissionProductivity,
                monthlyCommission,
                oneTimeDeposits,
                totalAnnualDeposit,
                potentialSavings,
                totalSavings,
                totalClients,
            } = await getProductTransfers(params);

            list.forEach((item: any) => fixDates(item.product.productTransfer));

            setList(list);
            setTotal(total || list.length);
            setTotalAnnualDeposit(totalAnnualDeposit);
            setTotalSavings(totalSavings);
            setTotalAgentCommissionSavings(totalAgentCommissionSavings);
            setTotalAgentCommissionProductivity(totalAgentCommissionProductivity);
            setTotalAgentMonthlyCommission(monthlyCommission);
            setTotalOneTimeDeposits(oneTimeDeposits);

            setPotentialSavings(potentialSavings);
            setTotalClients(totalClients);
            setItemsPerPage(itemsPerPage);

            if (!companies.length) {
                const result = await getProductTransferCompanies();
                setCompanies(result.companies || []);
            }

            if (queryParams.get('clientId') && !clientSearch) {
                setClientSearch(list[0]?.product?.client?.fullName);
            }

            setLoading(false);
        } catch (err: any) {
            setLoading(false);
            globalErrorHandler(err);
        }
    };

    const prevFiltersRef = useRef<string>('');
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Normalize dates for consistent serialization
        const normalizeDate = (date: Date | undefined) => (date ? date.toISOString() : null);

        // Create a stable string representation of filters to detect actual changes
        const currentFilters = JSON.stringify({
            clientSearch,
            status: status.join(','),
            toCompany: toCompany.join(','),
            toProduct: toProduct.join(','),
            search,
            active,
            action,
            submissionStartDate: normalizeDate(submissionStartDate),
            submissionEndDate: normalizeDate(submissionEndDate),
            lastChangeStartDate: normalizeDate(lastChangeStartDate),
            lastChangeEndDate: normalizeDate(lastChangeEndDate),
            closeStartDate: normalizeDate(closeStartDate),
            closeEndDate: normalizeDate(closeEndDate),
            agentSignedStartDate: normalizeDate(agentSignedStartDate),
            agentSignedEndDate: normalizeDate(agentSignedEndDate),
            employmentStatus,
            isInvestmentRound,
            agentIds: agentIds.join(','),
            oneTimeCommission,
            sortBy,
            asc,
        });

        // Skip if filters haven't actually changed
        if (prevFiltersRef.current === currentFilters) {
            return;
        }

        // Clear any pending debounce timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
            debounceTimeoutRef.current = null;
        }

        // Debounce the API call to ensure filters have stabilized (especially important during initialization)
        debounceTimeoutRef.current = setTimeout(
            () => {
                // Double-check filters haven't changed during the debounce period
                if (prevFiltersRef.current !== currentFilters) {
                    prevFiltersRef.current = currentFilters;
                    handleProductTransfers(0);
                }
                debounceTimeoutRef.current = null;
            },
            hasInitiallyLoaded.current ? 0 : 100,
        ); // Longer delay on initial load to allow state to stabilize

        if (!hasInitiallyLoaded.current) {
            hasInitiallyLoaded.current = true;
        }

        // Cleanup function to clear timeout if component unmounts or effect re-runs
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
                debounceTimeoutRef.current = null;
            }
        };
    }, [
        clientSearch,
        status,
        toCompany,
        toProduct,
        search,
        active,
        action,
        submissionStartDate,
        submissionEndDate,
        lastChangeStartDate,
        lastChangeEndDate,
        closeStartDate,
        closeEndDate,
        agentSignedStartDate,
        agentSignedEndDate,
        employmentStatus,
        isInvestmentRound,
        agentIds,
        oneTimeCommission,
        sortBy,
        asc,
    ]);

    const handleFreeSearch = async (search: string) => {
        if (search === '') {
            setClientSearch('');
        }
        setSearch(search);
    };

    const onToProductFilterSelected = (selectedOptions: readonly Option[]) => {
        setToProduct((selectedOptions || []).map((o: any) => o.value as string));
    };

    const onStatusFilterSelected = (selectedOptions: readonly Option[]) => {
        setStatus((selectedOptions || []).map((o: any) => o.value as string));
    };
    const onToCompanyFilterSelected = (selectedOptions: readonly Option[]) => {
        setToCompany((selectedOptions || []).map((o: any) => o.value as string));
    };

    const onActiveFilterSelected = (e: any) => {
        const isActive = e.target.value;
        setActive(isActive);
    };

    const onAutocompleteSelect = (value: any) => {
        setClientSearch(value);
    };

    const onExport = async () => {
        setIsLoadingExport(true);

        try {
            const result = await exportProductTransfers({
                search,
                ...(toCompany.length > 0 && { companyId: toCompany.join(',') }),
                productType: toProduct.join(','),
                isActive: active,
                action: action.join(','),
                isInvestmentRound,
                status: status.join(','),
                clientId: clientSearch,
                fromSubmissionDate: submissionStartDate?.toISOString(),
                toSubmissionDate: submissionEndDate?.toISOString(),
                fromLastChangeDate: lastChangeStartDate?.toISOString(),
                toLastChangeDate: lastChangeEndDate?.toISOString(),
                fromAgentSignatureDate: agentSignedStartDate?.toISOString(),
                toAgentSignatureDate: agentSignedEndDate?.toISOString(),
                fromCloseDate: closeStartDate?.toISOString(),
                toCloseDate: closeEndDate?.toISOString(),
                employmentStatus,
                ...(agentIds.length > 0 && { agentId: agentIds.join(',') }),
                oneTimeCommission,
            });
            const blob = await result.blob();
            const url = window.URL.createObjectURL(new Blob([blob], { type: blob.type }));
            const link = document.createElement('a');
            link.href = url;

            link.setAttribute('download', `product-transfers.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
        } catch (err) {
            globalErrorHandler(err);
        } finally {
            setIsLoadingExport(false);
        }
    };

    const onImport = async (files: FileType[]) => {
        setIsLoadingImport(true);

        try {
            const result = await uploadProductTransfers({ file: files[0] });
        } catch (err) {
            globalErrorHandler(err);
        } finally {
            setIsLoadingImport(false);
        }
    };
    const onSortChanged = (sortBy: string) => {
        const ascending = !sortBy.startsWith('-');
        const fieldName = sortBy.split('.').pop();
        if (fieldName) {
            setSortBy(fieldName);
            setAsc(ascending);
        }
        console.log(fieldName);
    };

    const [isUserPickingProducts, setIsUserPickingProducts] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState<string[]>([]);

    const onSuccessfulMail = (successfulSentStatuses: string[]) => {
        setIsUserPickingProducts(false);
        const updatedList = [...list];
        updatedList.forEach((item) => {
            if (successfulSentStatuses.includes(item.product.productTransfer._id)) {
                item.product.productTransfer.status = TransferStatuses.ProcessingAtCompany;
            }
        });
        setList(updatedList);
        setSelectedProducts([]);
    };

    const onRowClicked = (row: any) => {
        const { product } = row.original;
        const { productTransfer } = product;
        console.log(productTransfer._id, productTransfer);
        if (!isUserPickingProducts || productTransfer.status !== TransferStatuses.SignedByAgent) {
            return;
        }
        const index = selectedProducts.findIndex((id: string) => id === productTransfer._id);
        if (index >= 0) {
            selectedProducts.splice(index, 1);
        } else {
            selectedProducts.push(productTransfer._id);
        }
        setSelectedProducts([...selectedProducts]);
    };

    const rowStyle = (row: any) => {
        const { product } = row.original;
        const { productTransfer } = product;
        const syles: any = {};
        if (isUserPickingProducts && productTransfer.status === TransferStatuses.SignedByAgent) {
            syles.cursor = 'pointer';
        }
        if (selectedProducts.includes(productTransfer._id)) {
            syles.backgroundColor = '#e6f7ff';
        }
        return syles;
    };

    const getCompanyShortName = (company: Company) => {
        if (company.shortName && company.shortName.trim()) {
            if (company.type === CompanyTypes.insurance) {
                return `${company.shortName.trim()} (ביטוח)`;
            }
            return company.shortName.trim();
        }
        if (!company.name) return '';
        const separators = ['-', 'חברה'];
        for (const sep of separators) {
            if (company.name.includes(sep)) {
                return company.name.split(sep)[0].trim();
            }
        }
        return company.name.trim();
    };

    return (
        <React.Fragment>
            <div className="product-transfers">
                <PageTitle
                    breadCrumbItems={[
                        {
                            label: t('Product Transfers'),
                            path: '/productTransfers',
                            active: true,
                        },
                    ]}
                    title={t('Product Transfers')}
                />

                <Row className={i18n.dir()}>
                    <Col xl={12} lg={8}>
                        <Card>
                            <Card.Body>
                                {loading && <Loader />}

                                <Row className="mb-2">
                                    <Col md="3">
                                        <div className="form-label">{t('Search')}</div>
                                        <div className="search-bar form-inline">
                                            <ClientAutocomplete
                                                handleFreeSearch={handleFreeSearch}
                                                onAutocompleteSelect={onAutocompleteSelect}
                                                // defaultValue={queryParams.get('clientFullName') || ''}
                                            />
                                        </div>
                                    </Col>
                                    <Col md="auto">
                                        <div className="multi-select-filter-wrapper">
                                            <label className="form-label">{t('Status')}</label>
                                            <Select
                                                className="multi-select-filter mb-3"
                                                data-test-id="search-new-insurance-product"
                                                value={status.map((v) => ({
                                                    label: t(
                                                        camelCaseToText(
                                                            Object.keys(TransferStatuses).find((k) => (TransferStatuses as any)[k] === v) || '',
                                                        ),
                                                    ),
                                                    value: v,
                                                }))}
                                                options={Object.entries(TransferStatuses)
                                                    .slice(3)
                                                    .map((items: string[]) => ({
                                                        label: t(camelCaseToText(items[0])),
                                                        value: items[1],
                                                    }))}
                                                onChange={onStatusFilterSelected}
                                                isMulti
                                                placeholder={t('Select')}
                                                styles={{ container: (base) => ({ ...base, width: 260 }) }}
                                            />
                                        </div>
                                    </Col>
                                    <Col md="auto">
                                        <div className="form-label">{t('Last change date')}</div>
                                        <DateRangePicker
                                            placement="auto"
                                            format="dd/MM/yyyy"
                                            placeholder={t('Last change')}
                                            defaultValue={
                                                lastChangeStartDate && lastChangeEndDate ? [lastChangeStartDate, lastChangeEndDate] : undefined
                                            }
                                            onOk={(date: DateRange) => {
                                                setLastChangeStartDate(date[0]);
                                                setLastChangeEndDate(date[1]);
                                            }}
                                            onClean={(event: React.MouseEvent) => {
                                                setLastChangeStartDate(undefined);
                                                setLastChangeEndDate(undefined);
                                            }}
                                        />
                                    </Col>
                                    <Col md="auto">
                                        <div className="form-label">{t('Submission date')}</div>
                                        <DateRangePicker
                                            placement="auto"
                                            data-test-id="submission-date-range-picker"
                                            format="dd/MM/yyyy"
                                            placeholder={t('Submission date')}
                                            defaultValue={
                                                submissionStartDate && submissionEndDate ? [submissionStartDate, submissionEndDate] : undefined
                                            }
                                            onOk={(date: DateRange) => {
                                                setSubmissionStartDate(date[0]);
                                                setSubmissionEndDate(date[1]);
                                            }}
                                            onClean={(event: React.MouseEvent) => {
                                                setSubmissionStartDate(undefined);
                                                setSubmissionEndDate(undefined);
                                            }}
                                        />
                                    </Col>
                                    <Col md="auto">
                                        <div className="form-label">{t('Agent signed date')}</div>
                                        <DateRangePicker
                                            placement="auto"
                                            data-test-id="client-signed-date-range-picker"
                                            format="dd/MM/yyyy"
                                            placeholder={t('Agent signed date')}
                                            defaultValue={
                                                agentSignedStartDate && agentSignedEndDate ? [agentSignedStartDate, agentSignedEndDate] : undefined
                                            }
                                            onOk={(date: DateRange) => {
                                                setAgentSignedStartDate(date[0]);
                                                setAgentSignedEndDate(date[1]);
                                            }}
                                            onClean={(event: React.MouseEvent) => {
                                                setAgentSignedStartDate(undefined);
                                                setAgentSignedEndDate(undefined);
                                            }}
                                        />
                                    </Col>

                                    <Col md="auto">
                                        <div className="form-label">{t('Close date')}</div>
                                        <DateRangePicker
                                            placement="auto"
                                            data-test-id="close-date-range-picker"
                                            format="dd/MM/yyyy"
                                            placeholder={t('Close date')}
                                            defaultValue={closeStartDate && closeEndDate ? [closeStartDate, closeEndDate] : undefined}
                                            onOk={(date: DateRange) => {
                                                setCloseStartDate(date[0]);
                                                setCloseEndDate(date[1]);
                                            }}
                                            onClean={(event: React.MouseEvent) => {
                                                setCloseStartDate(undefined);
                                                setCloseEndDate(undefined);
                                            }}
                                        />
                                    </Col>
                                    <Col md="auto">
                                        <div className="multi-select-filter-wrapper">
                                            <label htmlFor="company-multi-select" className="form-label">
                                                {t('To company')}
                                            </label>
                                            <Select
                                                className="multi-select-filter mb-3"
                                                data-test-id="search-new-insurance-company"
                                                value={toCompany.map((id) => {
                                                    const c = companies.find((cc: Company) => cc._id === id);
                                                    return { label: c ? getCompanyShortName(c) : id, value: id };
                                                })}
                                                options={companies.map((c: Company) => ({
                                                    label: getCompanyShortName(c),
                                                    value: c._id,
                                                }))}
                                                onChange={onToCompanyFilterSelected}
                                                isMulti
                                                placeholder={t('Select')}
                                            />
                                        </div>
                                    </Col>
                                    <Col md="auto">
                                        <div className="multi-select-filter-wrapper">
                                            <label htmlFor="product-type-multi-select" className="form-label">
                                                {t('Product type')}
                                            </label>
                                            <Select
                                                className="multi-select-filter mb-3"
                                                data-test-id="search-new-insurance-product"
                                                value={toProduct.map((v) => ({
                                                    label: t(
                                                        camelCaseToText(
                                                            Object.keys(PensionProductTypes).find((k) => (PensionProductTypes as any)[k] === v) || '',
                                                        ),
                                                    ),
                                                    value: v,
                                                }))}
                                                options={Object.entries(PensionProductTypes).map((items: string[]) => ({
                                                    label: t(camelCaseToText(items[0])),
                                                    value: items[1],
                                                }))}
                                                onChange={onToProductFilterSelected}
                                                isMulti
                                                placeholder={t('Select')}
                                                styles={{ container: (base) => ({ ...base, width: 260 }) }}
                                            />
                                        </div>
                                    </Col>

                                    <Col md="auto">
                                        <div className="multi-select-filter-wrapper">
                                            <label className="form-label">{t('Action non active')}</label>
                                            <Select
                                                className="multi-select-filter mb-3"
                                                options={[
                                                    { label: t('All'), value: '' },
                                                    { label: t('Active'), value: 'true' },
                                                    { label: t('Inactive'), value: 'false' },
                                                ]}
                                                value={{
                                                    label: active === 'true' ? t('Active') : active === 'false' ? t('Inactive') : t('All'),
                                                    value: active,
                                                }}
                                                onChange={(opt: any) => setActive(opt?.value || '')}
                                                placeholder={t('All')}
                                            />
                                        </div>
                                    </Col>

                                    <Col md="auto">
                                        <div className="multi-select-filter-wrapper">
                                            <label className="form-label">{t('Action')}</label>
                                            <Select
                                                className="multi-select-filter mb-3"
                                                isMulti
                                                options={[
                                                    { label: t('transfer'), value: 'transfer' },
                                                    { label: t('b2'), value: 'agentAssignment' },
                                                    { label: t('newProduct'), value: 'newProduct' },
                                                    { label: t('newDeposit'), value: 'newDeposit' },
                                                ]}
                                                value={action.map((a) => ({
                                                    label:
                                                        a === 'transfer'
                                                            ? t('transfer')
                                                            : a === 'agentAssignment'
                                                              ? t('b2')
                                                              : a === 'newProduct'
                                                                ? t('newProduct')
                                                                : t('newDeposit'),
                                                    value: a,
                                                }))}
                                                onChange={(opts: any) => setAction((opts || []).map((o: any) => o.value))}
                                                placeholder={t('All')}
                                            />
                                        </div>
                                    </Col>

                                    <Col md="auto">
                                        <div className="multi-select-filter-wrapper">
                                            <label className="form-label">{t('Employment status')}</label>
                                            <Select
                                                className="multi-select-filter mb-3"
                                                options={[
                                                    { label: t('All'), value: '' },
                                                    ...EmploymentStatuses.map((s) => ({ label: t(camelCaseToText(s)), value: s })),
                                                ]}
                                                value={{
                                                    label: employmentStatus ? t(camelCaseToText(employmentStatus)) : t('All'),
                                                    value: employmentStatus,
                                                }}
                                                onChange={(opt: any) => setEmploymentStatus(opt?.value || '')}
                                                placeholder={t('All')}
                                            />
                                        </div>
                                    </Col>

                                    <Col md="auto">
                                        <div className="multi-select-filter-wrapper">
                                            <label className="form-label">{t('Pension/Investment')}</label>
                                            <Select
                                                className="multi-select-filter mb-3"
                                                options={[
                                                    { label: t('All'), value: '' },
                                                    { label: t('Investments'), value: 'true' },
                                                    { label: t('Pension'), value: 'false' },
                                                ]}
                                                value={{
                                                    label:
                                                        isInvestmentRound === 'true'
                                                            ? t('Investments')
                                                            : isInvestmentRound === 'false'
                                                              ? t('Pension')
                                                              : t('All'),
                                                    value: isInvestmentRound,
                                                }}
                                                onChange={(opt: any) => setIsInvestmentRound(opt?.value || '')}
                                                placeholder={t('All')}
                                            />
                                        </div>
                                    </Col>
                                    <Col md="auto">
                                        <div className="multi-select-filter-wrapper">
                                            <label className="form-label">{t('Agent')}</label>
                                            <Select
                                                className="multi-select-filter mb-3"
                                                options={agents.map((agent: User) => ({ label: agent.fullName || '', value: agent._id }))}
                                                value={agentIds.map((id) => ({
                                                    label: agents.find((a) => a._id === id)?.fullName || '',
                                                    value: id,
                                                }))}
                                                onChange={(opts: any) => setAgentIds((opts || []).map((o: any) => o.value))}
                                                isMulti
                                                placeholder={t('Select')}
                                                styles={{ container: (base) => ({ ...base, width: 260 }) }}
                                            />
                                        </div>
                                    </Col>
                                    {user.isAdmin && (
                                        <Col md="auto">
                                            <div className="multi-select-filter-wrapper">
                                                <label className="form-label">{t('עמלת היקף')}</label>
                                                <Select
                                                    className="multi-select-filter mb-3"
                                                    options={oneTimeCommissionOptions}
                                                    value={
                                                        oneTimeCommissionOptions.find((opt) => opt.value === oneTimeCommission) ||
                                                        oneTimeCommissionOptions[0]
                                                    }
                                                    onChange={(opt: any) => setOneTimeCommission(opt?.value || '')}
                                                    placeholder={t('All')}
                                                />
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                                <Row className="mb-3">
                                    <Col>
                                        <Dropzone
                                            maxFiles={1}
                                            // onFileUpload={onFileUpload}
                                            onDrop={(acceptedFiles) => onImport(acceptedFiles)}
                                        >
                                            {({ getRootProps, getInputProps }) => (
                                                <div className={'upload-btn end'}>
                                                    <div className="dz-message" {...getRootProps()}>
                                                        <LaddaButton
                                                            data-test-id="import-product-transfers"
                                                            loading={isLoadingImport}
                                                            data-style={EXPAND_LEFT}
                                                            className="btn btn-primary grouped-btn negative right"
                                                            data-spinner-color="#4d78cd"
                                                            dir="ltr"
                                                        >
                                                            {!isLoadingImport ? <i className="mdi mdi-cloud-upload-outline me-1" /> : null}
                                                            {t('Import')}
                                                        </LaddaButton>
                                                    </div>
                                                    <div className="needsclick" {...getRootProps()}>
                                                        <input {...getInputProps()} />
                                                    </div>
                                                </div>
                                            )}
                                        </Dropzone>

                                        <LaddaButton
                                            data-test-id="export-product-transfers"
                                            loading={isLoadingExport}
                                            data-style={EXPAND_LEFT}
                                            className={'btn btn-primary grouped-btn end'}
                                            dir="ltr"
                                            onClick={() => onExport()}
                                        >
                                            {!isLoadingExport ? <i className="mdi mdi-download me-1" /> : null}
                                            {t('Export')}
                                        </LaddaButton>

                                        <MailingButtonsAndLogic
                                            selectedProducts={selectedProducts}
                                            setSelectedProducts={setSelectedProducts}
                                            setIsUserPickingProducts={setIsUserPickingProducts}
                                            isUserPickingProducts={isUserPickingProducts}
                                            setLoading={setLoading}
                                            setStatus={setStatus}
                                            allowedStatus={TransferStatuses.SignedByAgent}
                                            onSuccessfulMail={onSuccessfulMail}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <div>
                                            <strong>{t('Total products')}</strong>
                                        </div>
                                        <div>{addCommas(total)}</div>
                                    </Col>
                                    <Col>
                                        <div>
                                            <strong>{t('Total clients')}</strong>
                                        </div>
                                        <div>{addCommas(totalClients)}</div>
                                    </Col>

                                    <Col>
                                        <div>
                                            <strong>{t('Potential savings')}</strong>
                                        </div>
                                        <div>₪{addCommas(potentialSavings)}</div>
                                    </Col>
                                    <Col>
                                        <div>
                                            <strong>{t('Total savings')}</strong>
                                        </div>
                                        <div>₪{addCommas(totalSavings)}</div>
                                    </Col>
                                    {/* <Col>
                                        {t('Total one time deposits')}: ₪{addCommas(totalOneTimeDeposits)}
                                    </Col> */}
                                    <Col>
                                        <div>
                                            <strong>{t('Total one time deposits')}</strong>
                                        </div>
                                        <div>₪{addCommas(totalOneTimeDeposits)}</div>
                                    </Col>
                                    <Col>
                                        <div>
                                            <strong>{t('Total annual deposit')}</strong>
                                        </div>
                                        <div>₪{addCommas(totalAnnualDeposit)}</div>
                                    </Col>

                                    {user.isAdmin && (
                                        <>
                                            <Col>
                                                <div>
                                                    <strong>{t('Total monthly commission')}</strong>
                                                </div>
                                                <div>₪{addCommas(totalAgentMonthlyCommission || 0)}</div>
                                            </Col>
                                            <Col>
                                                <div>
                                                    <strong>{t('Total commission savings')}</strong>
                                                </div>
                                                <div>₪{addCommas(totalAgentCommissionSavings || 0)}</div>
                                            </Col>
                                            <Col>
                                                <div>
                                                    <strong>{t('Total commission productivity')}</strong>
                                                </div>
                                                <div>₪{addCommas(totalAgentCommissionProductivity || 0)}</div>
                                            </Col>
                                            <Col>
                                                <div>
                                                    <strong>{t('Total onetime commission')}</strong>
                                                </div>
                                                <div>₪{addCommas((totalAgentCommissionSavings || 0) + (totalAgentCommissionProductivity || 0))}</div>
                                            </Col>
                                        </>
                                    )}
                                </Row>
                                {itemsPerPage ? (
                                    <Table
                                        columns={columns}
                                        paginationProps={{
                                            onPageSelected: handleProductTransfers,
                                            totalItems: total,
                                            numberOfPages: Math.ceil(total / itemsPerPage),
                                        }}
                                        isExpandable={true}
                                        // onRowClicked={(row) => oneEditProductTransfer(row.original.productTransfer)}
                                        data={list}
                                        onRowClicked={onRowClicked}
                                        rowStyle={rowStyle}
                                        pageSize={itemsPerPage}
                                        isSortable={true}
                                        ExpandedRow={ExpandedRow}
                                        pagination={true}
                                        isSelectable={false}
                                        tableClass="table-nowrap table-productTransfers"
                                        onSortChanged={onSortChanged}
                                        rowEqualityFn={(idOfCurrent: string, otherRow: any) => idOfCurrent === otherRow.product?.productTransfer._id}
                                    />
                                ) : null}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* add customer modal */}
                {/*<AddProductTransfer show={showAddProductTransfer} onHide={onCloseAddProductTransferModal} onAddProductTransfer={onAddProductTransfer} productTransfer={selectedProductTransfer}/>*/}
            </div>
        </React.Fragment>
    );
});

export default ProductTransfers;
